interface EnvironmentConfig {
  NODE_ENV: 'development' | 'production' | 'test';
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_AUTH_BYPASS_ENABLED: string;
  VITE_AUTH_BYPASS_EMAIL: string;
  VITE_CASHFREE_APP_ID: string;
  VITE_CASHFREE_SECRET_KEY: string;
  VITE_GOOGLE_SHEETS_API_KEY: string;
  VITE_GOOGLE_SHEETS_SPREADSHEET_ID: string;
}

const AUTH_BYPASS_SIGNED_OUT_KEY = 'STRATIBLE_AUTH_BYPASS_SIGNED_OUT';

/** One-time: `?stratible_auth_bypass=1` sets localStorage so bypass works without redeploying. */
function initAuthBypassFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = String(params.get('stratible_auth_bypass') || '').trim().toLowerCase();
    if (raw === '1' || raw === 'true' || raw === 'yes') {
      window.localStorage.setItem('STRATIBLE_AUTH_BYPASS', 'true');
      window.localStorage.removeItem(AUTH_BYPASS_SIGNED_OUT_KEY);
      params.delete('stratible_auth_bypass');
      const q = params.toString();
      const next = window.location.pathname + (q ? `?${q}` : '') + window.location.hash;
      window.history.replaceState({}, '', next);
    }
  } catch {
    /* ignore */
  }
}

initAuthBypassFromUrl();

const config: EnvironmentConfig = {
  NODE_ENV: (import.meta.env.NODE_ENV as EnvironmentConfig['NODE_ENV']) || 'development',
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  VITE_AUTH_BYPASS_ENABLED: import.meta.env.VITE_AUTH_BYPASS_ENABLED || 'false',
  VITE_AUTH_BYPASS_EMAIL: import.meta.env.VITE_AUTH_BYPASS_EMAIL || 'admin@bujji.in',
  VITE_CASHFREE_APP_ID: import.meta.env.VITE_CASHFREE_APP_ID || '',
  VITE_CASHFREE_SECRET_KEY: import.meta.env.VITE_CASHFREE_SECRET_KEY || '',
  VITE_GOOGLE_SHEETS_API_KEY: import.meta.env.VITE_GOOGLE_SHEETS_API_KEY || '',
  VITE_GOOGLE_SHEETS_SPREADSHEET_ID: import.meta.env.VITE_GOOGLE_SHEETS_SPREADSHEET_ID || '',
};

// Validate required environment variables
const requiredEnvVars = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
  'VITE_CASHFREE_APP_ID',
  'VITE_CASHFREE_SECRET_KEY',
] as const;

requiredEnvVars.forEach((envVar) => {
  if (!config[envVar]) {
    console.warn(`Missing required environment variable: ${envVar}`);
    // Don't throw errors in production, just log warnings
    // This allows the app to load with limited functionality
  }
});

/** True when temp auth bypass is on (no Supabase Auth / REST for login). */
export function isAuthBypassEnabled(): boolean {
  const raw = String(import.meta.env.VITE_AUTH_BYPASS_ENABLED || '').trim().toLowerCase();
  if (['true', '1', 'yes', 'on'].includes(raw)) {
    return true;
  }
  try {
    if (typeof window !== 'undefined') {
      const ls = String(window.localStorage.getItem('STRATIBLE_AUTH_BYPASS') || '').trim().toLowerCase();
      if (ls === 'true' || ls === '1') {
        return true;
      }
    }
  } catch {
    /* private mode / no storage */
  }
  return false;
}

/** User chose Sign Out while temporary bypass was active; skip auto-login until they opt in again (URL param or clearing storage). */
export function isAuthBypassSuppressed(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const v = String(window.localStorage.getItem(AUTH_BYPASS_SIGNED_OUT_KEY) || '')
      .trim()
      .toLowerCase();
    return v === 'true' || v === '1';
  } catch {
    return false;
  }
}

/** Use bypass auto-login and bypass-only UX (not real Cognito/Supabase session). */
export function shouldApplyAuthBypass(): boolean {
  return isAuthBypassEnabled() && !isAuthBypassSuppressed();
}

/** Call when signing out of the temporary bypass user so reload does not log them back in. */
export function suppressAuthBypassAfterSignOut(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('STRATIBLE_AUTH_BYPASS');
    window.localStorage.setItem(AUTH_BYPASS_SIGNED_OUT_KEY, 'true');
  } catch {
    /* ignore */
  }
}

export default config; 
